import {
  type ReactNode,
  type ChangeEvent,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { TextField } from '@mui/material'
import { isKeyboardEvent, withPickupCode, cleanupBarcode } from '../helpers'

type Props = {
  /**
   * Some customers are using USB barcode scanners. These devices emit the barcode as keyboard events.
   * Therefor we will listen to keyboard events and emit them to an input so that JS will be able to
   * pick up these keyboard down events. You can disable this if you're using an device that natively
   * supports barcode scanner. For example the Android SPA application receives barcode by a custom event.
   *
   * @default true
   */
  enableLegacyBarcodeScanners?: boolean
  children: ReactNode
}

export type BarcodeEvent = {
  barcode: string
  charset: string
  codeId: string
  timestamp: string
  aimId: string
}

type DeviceContext = 'usb' | 'handheld-legacy' | 'handheld-native' | null

type BarcodeContext = {
  barcode: string | null
  pickupCode: string | null
  device: DeviceContext
}

const Context = createContext<BarcodeContext>({
  barcode: null,
  pickupCode: null,
  device: null,
})

const clearInputTimeout = 100

// TODO: Refactor this code after all devices have the new APK and the
// chainways are no longer in use
export const BarcodeLaserListenerProvider = ({ children, enableLegacyBarcodeScanners = true }: Props) => {
  const timeout = useRef<number | undefined>(undefined)
  const textInputRef = useRef<HTMLInputElement>(null)
  const [barcodeBuffer, setBarcodeBuffer] = useState('')
  const [barcode, setBarcode] = useState('')
  const [pickupCode, setPickupCode] = useState('')
  const [device, setDevice] = useState<DeviceContext>(null)

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setBarcodeBuffer(event.target.value)
  }

  useEffect(() => {
    // Start: New code for receiving the barcode from the hardware barcode scanner
    const barcodeListener = (ev: MessageEvent<BarcodeEvent>) => {
      const input = cleanupBarcode(ev.data.barcode)

      if (input) {
        setDevice('handheld-native')

        const code = withPickupCode(input)

        setPickupCode(code ? code.pickupCode || '' : '')
        // making sure any misconfigurated barcode scanner is not giving us a space before or after our barcode
        setBarcode(input.trim())

        // Clear the barcode value after components received value
        setTimeout(() => {
          setDevice(null)
          setPickupCode('')
          setBarcode('')
        }, clearInputTimeout)
      }
    }
    document.addEventListener('barcode', barcodeListener)
    // End: New code

    const keypressListener = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        setTimeout(() => {
          const code = withPickupCode(barcodeBuffer)

          setPickupCode(code ? code.pickupCode || '' : '')
          // making sure any misconfigurated barcode scanner is not giving us crap
          setBarcode(cleanupBarcode(barcodeBuffer))
        }, 0)

        // Clear the entered barcode, so a new value will be triggered when double scanning
        setTimeout(() => {
          setDevice(null)
          setPickupCode('')
          setBarcode('')
          setBarcodeBuffer('')
        }, clearInputTimeout)

        // assume that when the keydown trick does not work that we're scanning with USB device
      } else if (textInputRef.current !== document.activeElement) {
        setDevice('usb')
        setBarcodeBuffer((buffer) => `${buffer}${event.key}`)
      }

      if (timeout.current) {
        clearTimeout(timeout.current)
      }

      // To avoid holding keypresses from anywhere else than the scanner, clear the buffer
      timeout.current = window.setTimeout(() => {
        setBarcodeBuffer('')
      }, clearInputTimeout)
    }
    document.addEventListener('keypress', keypressListener)

    // Listen to the barcode scanner button on the side of the devices
    // The keydown event will happen before the keypress, when pressed the hidden
    // input field will receive the key strokes made by the laser scanner
    const keydownListener = (event: KeyboardEvent | InputEvent) => {
      if (
        enableLegacyBarcodeScanners &&
        // first make sure we are talking about our KeyboardEvent
        isKeyboardEvent(event) &&
        textInputRef.current &&
        textInputRef.current !== document.activeElement &&
        // The Honeywell and Chainway laser scan button is marked as 'Unidentified'
        // TODO: Look up for any standard for event.key in case this changes in the future
        event.key.toLowerCase() === 'unidentified' &&
        (event.target as HTMLElement)?.tagName?.toLowerCase() !== 'input'
      ) {
        textInputRef.current.readOnly = true
        textInputRef.current.focus()
        setTimeout(() => {
          textInputRef.current!.readOnly = false
          setDevice('handheld-legacy')
        }, 0)
      }
    }
    document.addEventListener('keydown', keydownListener)

    return () => {
      document.removeEventListener('keypress', keypressListener)
      document.removeEventListener('keydown', keydownListener)
      document.removeEventListener('barcode', barcodeListener)
    }
  }, [textInputRef, barcodeBuffer, setBarcode, setDevice, enableLegacyBarcodeScanners])

  const value = useMemo(
    () => ({
      // TODO: when we are sure its a barcode? because it can also be location or pickup etc, maybe barcode is not a correct name?
      barcode,
      pickupCode,
      device,
    }),
    [barcode, pickupCode, device]
  )

  return (
    <>
      <Context.Provider value={value}>{children}</Context.Provider>

      <TextField
        inputRef={textInputRef}
        value={barcodeBuffer}
        onChange={handleChange}
        data-testid="barcode-laser-listener"
        aria-hidden
        slotProps={{ htmlInput: { tabIndex: -1 } }}
        sx={{
          position: 'absolute',
          top: -1000,
          left: -1000,
          zIndex: 20000,
        }}
      />
    </>
  )
}

export const useBarcodeListener = () => useContext(Context)
